import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import FeedbackPopup from '../components/FeedbackPopup';
import { SEEDED_WAITLIST_COUNT, BACKEND_URL } from '../constants';
import { 
  Shield, 
  TrendingUp, 
  Bell, 
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  DollarSign,
  Eye,
  Activity,
  BarChart3,
  Calendar,
  Zap,
  Lock,
  CreditCard,
  FileText
} from 'lucide-react';
import '../styles/LandingPage.css';
import '../styles/BillAlertsPage.css';

const FIGMA_DEMO_URL = 'https://www.figma.com/proto/VFZ9YuB9BbNNhyLgHh9T3j/Money-Sentinel?node-id=0-1&t=bk5flBmOPkVTsnGl-1';

const BillAlertsPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [priorityProblem, setPriorityProblem] = useState('');
  const [otherProblem, setOtherProblem] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(SEEDED_WAITLIST_COUNT);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);

  // Fetch waitlist count
  useEffect(() => {
    const fetchWaitlistCount = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/waitlist/count`);
        const data = await response.json();
        setWaitlistCount(SEEDED_WAITLIST_COUNT + data.count);
      } catch (error) {
        // Keep default value
      }
    };
    
    fetchWaitlistCount();
  }, []);

  // Handle demo click
  const handleDemoClick = useCallback((url) => {
    localStorage.setItem('demoOpened', 'true');
    
    if (window.posthog) {
      window.posthog.capture('demo_clicked_bill_alerts', {
        source_page: '/bill-alerts'
      });
    }
    
    window.open(url, '_blank');
  }, []);

  // Check for demo feedback
  useEffect(() => {
    let timeoutId;
    let intervalId;
    
    const showPopupIfNeeded = (source = 'unknown') => {
      const demoOpened = localStorage.getItem('demoOpened');
      
      if (demoOpened === 'true' && !showFeedbackPopup) {
        if (timeoutId) clearTimeout(timeoutId);
        
        timeoutId = setTimeout(() => {
          setShowFeedbackPopup(true);
          
          if (window.posthog) {
            window.posthog.capture('demo_feedback_popup_shown', {
              trigger_source: source,
              source_page: '/bill-alerts'
            });
          }
          
          localStorage.removeItem('demoOpened');
        }, 2000);
      }
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        showPopupIfNeeded('visibilitychange');
      }
    };

    const handleFocus = () => {
      showPopupIfNeeded('focus');
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    showPopupIfNeeded('mount');
    
    intervalId = setInterval(() => {
      if (!document.hidden) {
        const demoOpened = localStorage.getItem('demoOpened');
        if (demoOpened === 'true' && !showFeedbackPopup) {
          showPopupIfNeeded('interval');
        }
      }
    }, 2000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [showFeedbackPopup]);

  const handleFeedbackSubmit = useCallback(async (feedbackData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(feedbackData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit feedback');
      }
      
      if (window.posthog) {
        window.posthog.capture('feedback_submitted_success', {
          source_page: '/bill-alerts'
        });
      }
    } catch (error) {
      if (window.posthog) {
        window.posthog.capture('feedback_submission_failed', {
          error: error.message,
          source_page: '/bill-alerts'
        });
      }
    }
  }, []);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    
    const submissionData = {
      email,
      country: country || null,
      priority_problem: priorityProblem === 'other' ? otherProblem : priorityProblem,
      source_page: '/bill-alerts',
      source_campaign: 'bill_increase_monitoring',
      signup_url: window.location.href,
      referrer_url: document.referrer || null,
      utm_source: urlParams.get('utm_source') || null,
      utm_medium: urlParams.get('utm_medium') || null,
      utm_campaign: urlParams.get('utm_campaign') || null,
      utm_content: urlParams.get('utm_content') || null,
      timestamp: new Date().toISOString(),
    };
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to join waitlist');
      }

      setIsSubmitting(false);
      setWaitlistCount(prev => prev + 1);
      
      if (window.posthog) {
        window.posthog.capture('waitlist_submitted_bill_alerts', {
          source_page: '/bill-alerts',
          source_campaign: 'bill_increase_monitoring',
          priority_problem: submissionData.priority_problem,
          has_utm: !!(urlParams.get('utm_source') || urlParams.get('utm_campaign'))
        });
      }
      
      // Redirect to survey
      navigate('/survey', {
        state: {
          email: submissionData.email,
          source_page: '/bill-alerts',
          source_campaign: 'bill_increase_monitoring',
          priority_problem: submissionData.priority_problem
        }
      });
      
    } catch (error) {
      const pendingSubmissions = JSON.parse(localStorage.getItem('pendingWaitlist') || '[]');
      pendingSubmissions.push(submissionData);
      localStorage.setItem('pendingWaitlist', JSON.stringify(pendingSubmissions));
      
      setIsSubmitting(false);
      
      if (window.posthog) {
        window.posthog.capture('waitlist_joined_offline', {
          saved_to_backup: true,
          source_page: '/bill-alerts',
          priority_problem: submissionData.priority_problem,
        });
      }
      
      // Redirect to survey even for offline signups
      navigate('/survey', {
        state: {
          email: submissionData.email,
          source_page: '/bill-alerts',
          source_campaign: 'bill_increase_monitoring',
          priority_problem: submissionData.priority_problem
        }
      });
    }
  };

  // Track page view
  useEffect(() => {
    if (window.posthog) {
      window.posthog.capture('page_view_bill_alerts', {
        page_path: '/bill-alerts',
        source_campaign: 'bill_increase_monitoring'
      });
    }
  }, []);

  return (
    <div className="bill-alerts-page">
      {/* Hero Section */}
      <section className="bill-hero-section">
        <div className="bill-hero-container">
          <div className="bill-hero-content">
            <h1 className="bill-hero-title">
              Stop Paying More Without Realizing It
            </h1>
            <p className="bill-hero-subtitle">
              MySentinel continuously monitors recurring bills, subscriptions, and household expenses 
              so you know when costs increase before they become expensive habits.
            </p>
            <p className="bill-hero-support">
              Most bill increases happen quietly. We make them visible.
            </p>
            
            <div className="bill-hero-ctas">
              <Button 
                onClick={() => document.getElementById('bill-waitlist')?.scrollIntoView({ behavior: 'smooth' })}
                className="bill-cta-primary"
              >
                Join Early Access
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                onClick={() => handleDemoClick(FIGMA_DEMO_URL)}
                variant="outline"
                className="bill-cta-secondary"
              >
                Explore Interactive Demo
              </Button>
            </div>
          </div>
          
          {/* Hero Alert Card */}
          <div className="bill-alert-demo">
            <Card className="bill-alert-card">
              <CardContent className="bill-alert-content">
                <div className="alert-header">
                  <TrendingUp className="alert-icon-increase" />
                  <span className="alert-badge-bill">Bill Increase Detected</span>
                </div>
                
                <div className="alert-body-bill">
                  <h3 className="alert-service">Netflix</h3>
                  
                  <div className="cost-comparison">
                    <div className="cost-row">
                      <span className="cost-label">Previous Monthly Cost:</span>
                      <span className="cost-value">£10.99</span>
                    </div>
                    <div className="cost-row">
                      <span className="cost-label">Current Monthly Cost:</span>
                      <span className="cost-value highlight">£14.99</span>
                    </div>
                    <div className="cost-row increase">
                      <span className="cost-label">Increase:</span>
                      <span className="cost-value increase">+36%</span>
                    </div>
                  </div>
                  
                  <div className="annual-impact">
                    <h4>Estimated Annual Impact:</h4>
                    <p className="impact-value">+£48</p>
                  </div>
                </div>
                
                <Button className="alert-action-btn-bill">
                  Review Increase
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="bill-problem-section">
        <div className="section-container">
          <h2 className="section-title">Small Increases Add Up Faster Than Most People Realize</h2>
          <p className="section-description-large">
            Most people notice large purchases.
          </p>
          
          <p className="section-description">
            Few people notice:
          </p>
          
          <div className="problem-grid">
            <div className="problem-card">
              <CheckCircle2 className="problem-icon" />
              <p className="problem-text">Subscriptions increasing by £2</p>
            </div>
            <div className="problem-card">
              <CheckCircle2 className="problem-icon" />
              <p className="problem-text">Insurance premiums quietly rising</p>
            </div>
            <div className="problem-card">
              <CheckCircle2 className="problem-icon" />
              <p className="problem-text">Utility costs creeping upward</p>
            </div>
            <div className="problem-card">
              <CheckCircle2 className="problem-icon" />
              <p className="problem-text">Telecom packages becoming more expensive</p>
            </div>
            <div className="problem-card">
              <CheckCircle2 className="problem-icon" />
              <p className="problem-text">Recurring services adding hidden costs</p>
            </div>
          </div>
          
          <p className="section-description">
            Because increases happen gradually, they often go unnoticed for months or even years.
          </p>
          
          <div className="bill-closing-message">
            <AlertCircle className="inline-icon-warning" />
            <p>The problem is rarely one large charge. It is dozens of small increases accumulating over time.</p>
          </div>
        </div>
      </section>

      {/* Financial Leak Example Section */}
      <section className="bill-leak-examples-section">
        <div className="section-container">
          <h2 className="section-title">Where Money Quietly Leaks Away</h2>
          
          <div className="leak-examples-grid">
            <Card className="leak-example-card">
              <CardContent className="leak-example-content">
                <div className="leak-category">
                  <Activity className="leak-icon" />
                  <h3>Streaming Services</h3>
                </div>
                <div className="leak-comparison">
                  <span className="leak-before">£10.99</span>
                  <ArrowRight className="leak-arrow" />
                  <span className="leak-after">£14.99</span>
                </div>
                <div className="leak-impact">+£48/year</div>
              </CardContent>
            </Card>

            <Card className="leak-example-card">
              <CardContent className="leak-example-content">
                <div className="leak-category">
                  <BarChart3 className="leak-icon" />
                  <h3>Broadband</h3>
                </div>
                <div className="leak-comparison">
                  <span className="leak-before">£39</span>
                  <ArrowRight className="leak-arrow" />
                  <span className="leak-after">£52</span>
                </div>
                <div className="leak-impact">+£156/year</div>
              </CardContent>
            </Card>

            <Card className="leak-example-card">
              <CardContent className="leak-example-content">
                <div className="leak-category">
                  <CreditCard className="leak-icon" />
                  <h3>Mobile Plan</h3>
                </div>
                <div className="leak-comparison">
                  <span className="leak-before">£22</span>
                  <ArrowRight className="leak-arrow" />
                  <span className="leak-after">£29</span>
                </div>
                <div className="leak-impact">+£84/year</div>
              </CardContent>
            </Card>

            <Card className="leak-example-card">
              <CardContent className="leak-example-content">
                <div className="leak-category">
                  <Shield className="leak-icon" />
                  <h3>Insurance Renewal</h3>
                </div>
                <div className="leak-comparison">
                  <span className="leak-before">£780</span>
                  <ArrowRight className="leak-arrow" />
                  <span className="leak-after">£960</span>
                </div>
                <div className="leak-impact">+£180/year</div>
              </CardContent>
            </Card>

            <Card className="leak-example-card">
              <CardContent className="leak-example-content">
                <div className="leak-category">
                  <Zap className="leak-icon" />
                  <h3>Software Subscription</h3>
                </div>
                <div className="leak-comparison">
                  <span className="leak-before">£8</span>
                  <ArrowRight className="leak-arrow" />
                  <span className="leak-after">£12</span>
                </div>
                <div className="leak-impact">+£48/year</div>
              </CardContent>
            </Card>
          </div>

          <div className="leak-highlight-message">
            <TrendingUp className="highlight-icon" />
            <p className="highlight-text">
              Individually these changes feel small. Together they can cost hundreds or thousands every year.
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="bill-solution-section">
        <div className="section-container">
          <div className="solution-header">
            <Bell className="solution-icon-large" />
            <h2 className="section-title">A Proactive Bill Monitoring Assistant</h2>
          </div>
          
          <p className="section-description-large">
            MySentinel continuously watches recurring payments and identifies changes before they become 
            long-term financial leaks.
          </p>
          
          <p className="section-description">
            Instead of discovering increases months later, you receive timely alerts and recommendations.
          </p>
          
          <div className="solution-features-grid">
            <Card className="solution-feature-card">
              <CardContent className="solution-feature-content">
                <TrendingUp className="solution-feature-icon" />
                <h3 className="solution-feature-title">Price increase detected</h3>
              </CardContent>
            </Card>
            
            <Card className="solution-feature-card">
              <CardContent className="solution-feature-content">
                <DollarSign className="solution-feature-icon" />
                <h3 className="solution-feature-title">Subscription cost changed</h3>
              </CardContent>
            </Card>
            
            <Card className="solution-feature-card">
              <CardContent className="solution-feature-content">
                <Shield className="solution-feature-icon" />
                <h3 className="solution-feature-title">Insurance renewal increased</h3>
              </CardContent>
            </Card>
            
            <Card className="solution-feature-card">
              <CardContent className="solution-feature-content">
                <BarChart3 className="solution-feature-icon" />
                <h3 className="solution-feature-title">Utility bill exceeds historical average</h3>
              </CardContent>
            </Card>
            
            <Card className="solution-feature-card">
              <CardContent className="solution-feature-content">
                <Bell className="solution-feature-icon" />
                <h3 className="solution-feature-title">New recurring charge discovered</h3>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Subscription Price Increase Section */}
      <section className="bill-subscription-section">
        <div className="section-container">
          <h2 className="section-title">Detect Subscription Price Hikes Automatically</h2>
          <p className="section-description">
            Many subscriptions gradually increase pricing over time.
          </p>
          
          <div className="subscription-types">
            <span className="subscription-tag">Streaming services</span>
            <span className="subscription-tag">Cloud storage</span>
            <span className="subscription-tag">Productivity tools</span>
            <span className="subscription-tag">Software subscriptions</span>
            <span className="subscription-tag">Memberships</span>
          </div>
          
          <p className="section-description">
            MySentinel compares historical transactions and highlights increases that might otherwise go unnoticed.
          </p>
          
          <div className="subscription-example">
            <Card className="subscription-example-card">
              <CardContent className="subscription-example-content">
                <div className="subscription-header">
                  <Activity className="subscription-icon" />
                  <h3>Spotify</h3>
                </div>
                
                <div className="subscription-comparison">
                  <div className="comparison-item">
                    <span className="comparison-label">Last Year:</span>
                    <span className="comparison-value">£9.99</span>
                  </div>
                  <div className="comparison-item">
                    <span className="comparison-label">Current:</span>
                    <span className="comparison-value highlight">£12.99</span>
                  </div>
                  <div className="comparison-item increase">
                    <span className="comparison-label">Increase:</span>
                    <span className="comparison-value">+30%</span>
                  </div>
                </div>
                
                <div className="subscription-annual">
                  <span className="annual-label">Estimated Annual Difference:</span>
                  <span className="annual-value">£36</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Monitor Everything Section */}
      <section className="bill-monitor-everything-section">
        <div className="section-container">
          <h2 className="section-title">Monitor Everything. Not Just One Account.</h2>
          <p className="section-description-large">
            Financial leaks rarely exist in a single place.
          </p>
          
          <p className="monitor-intro-text">
            Most people have multiple:
          </p>
          
          <div className="multiple-items-grid">
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Bank accounts</span>
            </div>
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Credit cards</span>
            </div>
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Subscriptions</span>
            </div>
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Recurring bills</span>
            </div>
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Service providers</span>
            </div>
          </div>
          
          <p className="monitor-pattern-text">
            Reviewing them individually makes it easy to miss important patterns.
          </p>
          
          <p className="section-description-large">
            MySentinel is designed to securely monitor your connected financial accounts together and identify 
            opportunities, risks, and money leaks across your entire financial ecosystem.
          </p>
          
          <div className="monitor-cards-grid">
            <Card className="monitor-card">
              <CardContent className="monitor-card-content">
                <Activity className="monitor-card-icon" />
                <h3 className="monitor-card-title">Bank Accounts</h3>
                <p className="monitor-card-description">
                  Monitor multiple connected accounts in one place.
                </p>
              </CardContent>
            </Card>

            <Card className="monitor-card">
              <CardContent className="monitor-card-content">
                <CreditCard className="monitor-card-icon" />
                <h3 className="monitor-card-title">Credit Cards</h3>
                <p className="monitor-card-description">
                  Track utilization, balances, and interest risks across cards.
                </p>
              </CardContent>
            </Card>

            <Card className="monitor-card">
              <CardContent className="monitor-card-content">
                <Bell className="monitor-card-icon" />
                <h3 className="monitor-card-title">Subscriptions</h3>
                <p className="monitor-card-description">
                  Detect recurring charges and forgotten services.
                </p>
              </CardContent>
            </Card>

            <Card className="monitor-card">
              <CardContent className="monitor-card-content">
                <TrendingUp className="monitor-card-icon" />
                <h3 className="monitor-card-title">Bills</h3>
                <p className="monitor-card-description">
                  Monitor telecom, insurance, utilities, and other recurring expenses.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="monitor-highlight-box">
            <Shield className="monitor-highlight-icon" />
            <p className="monitor-highlight-text">Connect once. Monitor everything.</p>
          </div>
          
          <div className="monitor-supporting-content">
            <p className="section-description-large">
              The most valuable financial insights often appear when information is viewed across multiple 
              accounts rather than in isolation.
            </p>
            
            <h3 className="monitor-examples-title">Examples:</h3>
            <div className="monitor-examples">
              <div className="monitor-example">
                <ArrowRight className="monitor-example-icon" />
                <span>One card approaching high utilization while another remains underused</span>
              </div>
              <div className="monitor-example">
                <ArrowRight className="monitor-example-icon" />
                <span>Duplicate subscriptions across different payment methods</span>
              </div>
              <div className="monitor-example">
                <ArrowRight className="monitor-example-icon" />
                <span>Rising recurring costs spread across multiple providers</span>
              </div>
              <div className="monitor-example">
                <ArrowRight className="monitor-example-icon" />
                <span>Hidden money leaks that are difficult to see when reviewing accounts individually</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Renewal Monitoring Section */}
      <section className="bill-insurance-section">
        <div className="section-container">
          <h2 className="section-title">Catch Insurance Increases Before Renewal</h2>
          <p className="section-description-large">
            Many people renew insurance without comparing the new premium against previous years.
          </p>
          
          <p className="section-description">
            MySentinel highlights:
          </p>
          
          <div className="insurance-features">
            <div className="insurance-feature">
              <CheckCircle2 className="feature-check" />
              <span>Policy renewal increases</span>
            </div>
            <div className="insurance-feature">
              <CheckCircle2 className="feature-check" />
              <span>Unusual premium jumps</span>
            </div>
            <div className="insurance-feature">
              <CheckCircle2 className="feature-check" />
              <span>Recurring insurance inflation</span>
            </div>
          </div>
          
          <div className="insurance-example">
            <Card className="insurance-example-card">
              <CardContent className="insurance-example-content">
                <div className="insurance-header">
                  <Shield className="insurance-icon" />
                  <h3>Home Insurance</h3>
                </div>
                
                <div className="insurance-comparison">
                  <div className="comparison-row">
                    <span className="comparison-label">Previous Premium:</span>
                    <span className="comparison-value">£850</span>
                  </div>
                  <div className="comparison-row">
                    <span className="comparison-label">Renewal Premium:</span>
                    <span className="comparison-value highlight">£1,020</span>
                  </div>
                  <div className="comparison-row increase">
                    <span className="comparison-label">Increase:</span>
                    <span className="comparison-value">+20%</span>
                  </div>
                </div>
                
                <div className="insurance-opportunity">
                  <CheckCircle2 className="opportunity-icon" />
                  <span>Potential Savings Opportunity Detected</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Annual Financial Impact Section */}
      <section className="bill-annual-impact-section">
        <div className="section-container">
          <h2 className="section-title">Small Bill Increases Can Become Large Annual Costs</h2>
          <p className="section-description-large">
            Most households do not lose money because of one major financial mistake.
          </p>
          <p className="section-description">
            They lose money through dozens of unnoticed increases.
          </p>
          
          <div className="annual-impact-illustration">
            <Card className="annual-impact-card">
              <CardContent className="annual-impact-content">
                <h3 className="impact-title">Example Illustration</h3>
                
                <div className="impact-breakdown">
                  <div className="impact-row">
                    <span className="impact-category">Streaming:</span>
                    <span className="impact-amount">+£48/year</span>
                  </div>
                  <div className="impact-row">
                    <span className="impact-category">Broadband:</span>
                    <span className="impact-amount">+£156/year</span>
                  </div>
                  <div className="impact-row">
                    <span className="impact-category">Insurance:</span>
                    <span className="impact-amount">+£170/year</span>
                  </div>
                  <div className="impact-row">
                    <span className="impact-category">Mobile:</span>
                    <span className="impact-amount">+£84/year</span>
                  </div>
                  <div className="impact-row">
                    <span className="impact-category">Software:</span>
                    <span className="impact-amount">+£60/year</span>
                  </div>
                </div>
                
                <div className="impact-total">
                  <span className="total-label">Total Additional Cost:</span>
                  <span className="total-amount">£518/year</span>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="annual-highlight">
            <TrendingUp className="highlight-icon" />
            <p className="highlight-text">
              What begins as a few pounds each month can become hundreds of pounds every year.
            </p>
          </div>
        </div>
      </section>

      {/* Why Continuous Monitoring Matters */}
      <section className="bill-monitoring-section">
        <div className="section-container">
          <h2 className="section-title">Bills Change Continuously</h2>
          <p className="section-description-large">
            Even financially disciplined people:
          </p>
          
          <div className="monitoring-challenges">
            <div className="challenge-item">
              <Eye className="challenge-icon" />
              <span>Miss renewal notices</span>
            </div>
            <div className="challenge-item">
              <TrendingUp className="challenge-icon" />
              <span>Overlook subscription increases</span>
            </div>
            <div className="challenge-item">
              <Activity className="challenge-icon" />
              <span>Stop reviewing recurring charges</span>
            </div>
            <div className="challenge-item">
              <CheckCircle2 className="challenge-icon" />
              <span>Assume costs remain unchanged</span>
            </div>
          </div>
          
          <div className="monitoring-message">
            <p className="section-description">
              Financial leaks are not a one-time problem.
            </p>
            <p className="section-description">
              Monitoring should not be one-time either.
            </p>
          </div>
          
          <div className="monitoring-solution">
            <Card className="monitoring-solution-card">
              <CardContent className="monitoring-solution-content">
                <Bell className="solution-bell-icon" />
                <h3>MySentinel continuously watches for new increases and alerts you when action may be needed.</h3>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust and Privacy Section */}
      <section className="bill-trust-section">
        <div className="section-container">
          <h2 className="section-title">Built for Trust and Privacy</h2>
          <p className="section-description">
            Financial tools require a high level of trust.
          </p>
          <p className="section-description">
            This platform is designed with a privacy-first approach to ensure your financial data is secure and under your control.
          </p>
          
          <div className="trust-grid">
            <Card className="trust-card">
              <CardContent className="trust-card-content">
                <div className="trust-icon-wrapper">
                  <Eye className="trust-icon" />
                </div>
                <h3 className="trust-card-title">Read-only Access</h3>
                <p className="trust-card-description">
                  We only analyze your financial data. We cannot move or modify your money.
                </p>
              </CardContent>
            </Card>
            
            <Card className="trust-card">
              <CardContent className="trust-card-content">
                <div className="trust-icon-wrapper">
                  <Shield className="trust-icon" />
                </div>
                <h3 className="trust-card-title">No Money Movement</h3>
                <p className="trust-card-description">
                  The system does not initiate transactions or transfers.
                </p>
              </CardContent>
            </Card>
            
            <Card className="trust-card">
              <CardContent className="trust-card-content">
                <div className="trust-icon-wrapper">
                  <Lock className="trust-icon" />
                </div>
                <h3 className="trust-card-title">No Data Selling</h3>
                <p className="trust-card-description">
                  Your personal financial data is never sold or shared with third parties.
                </p>
              </CardContent>
            </Card>
            
            <Card className="trust-card">
              <CardContent className="trust-card-content">
                <div className="trust-icon-wrapper">
                  <Zap className="trust-icon" />
                </div>
                <h3 className="trust-card-title">On-device Processing</h3>
                <p className="trust-card-description">
                  Sensitive insights are generated locally on your device to minimize data exposure (where possible).
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="trust-footer">
            <Shield className="trust-footer-icon" />
            <div className="trust-footer-text">
              <p>We only analyze financial activity to generate insights.</p>
              <p>We cannot move, withdraw, or transfer your money.</p>
              <p>Your financial data belongs to you.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Demo Section */}
      <section className="bill-demo-section">
        <div className="section-container">
          <h2 className="section-title">Explore the Interactive Demo</h2>
          <p className="section-description">
            See how MySentinel detects bill increases, subscription price hikes, and recurring spending leaks.
          </p>
          
          <Button 
            onClick={() => handleDemoClick(FIGMA_DEMO_URL)}
            className="demo-cta-btn"
            size="lg"
          >
            Explore Interactive Demo
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Waitlist Section */}
      <section id="bill-waitlist" className="bill-waitlist-section">
        <div className="section-container">
          <div className="waitlist-header">
            <h2 className="section-title">Join the Waitlist</h2>
            <p className="waitlist-count">{waitlistCount.toLocaleString()} people waiting</p>
          </div>
          
          <div className="waitlist-form-container">
            {submitSuccess ? (
              <div className="success-message">
                <CheckCircle2 className="success-icon" />
                <h3>Thanks for joining!</h3>
                <p>You&apos;re on the list to help us catch bill increases automatically.</p>
              </div>
            ) : (
              <form onSubmit={handleWaitlistSubmit} className="waitlist-form">
                <div className="form-group">
                  <label htmlFor="bill-email" className="form-label">Email *</label>
                  <Input
                    id="bill-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="bill-country" className="form-label">Country (optional)</label>
                  <Input
                    id="bill-country"
                    type="text"
                    placeholder="e.g., United States"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group priority-problem-group">
                  <label className="form-label form-label-required">
                    What would make you sign up today? *
                  </label>
                  <p className="form-sublabel">
                    Select the one problem you would most like solved automatically.
                  </p>
                  <div className="radio-options">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="trial_reminder"
                        checked={priorityProblem === 'trial_reminder'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Never forget a free trial again</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="bill_increase"
                        checked={priorityProblem === 'bill_increase'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Catch bill increases automatically</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="credit_protection"
                        checked={priorityProblem === 'credit_protection'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Protect my credit score automatically</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="reduce_interest"
                        checked={priorityProblem === 'reduce_interest'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Reduce unnecessary credit card interest</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="find_subscriptions"
                        checked={priorityProblem === 'find_subscriptions'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Find hidden subscriptions and recurring charges</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="continuous_monitoring"
                        checked={priorityProblem === 'continuous_monitoring'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Continuously monitor my finances for money leaks</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="other"
                        checked={priorityProblem === 'other'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Other</span>
                    </label>
                    
                    {priorityProblem === 'other' && (
                      <div className="other-problem-input">
                        <Input
                          type="text"
                          placeholder="Please specify"
                          value={otherProblem}
                          onChange={(e) => setOtherProblem(e.target.value)}
                          required
                          className="form-input"
                        />
                      </div>
                    )}
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="submit-btn"
                  size="lg"
                >
                  {isSubmitting ? 'Joining...' : 'Join Early Access'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Demo CTA After Waitlist - NEW REQUIREMENT */}
      <section className="bill-bottom-demo-section">
        <div className="section-container">
          <h2 className="section-title">Not Ready to Join Yet?</h2>
          <p className="section-description">
            See exactly how MySentinel works before deciding.
          </p>
          
          <Button 
            onClick={() => {
              if (window.posthog) {
                window.posthog.capture('bottom_demo_clicked_bill_alerts', {
                  source_page: '/bill-alerts'
                });
              }
              handleDemoClick(FIGMA_DEMO_URL);
            }}
            className="bottom-demo-btn"
            size="lg"
            variant="outline"
          >
            Try Interactive Demo
          </Button>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="bill-final-cta-section">
        <div className="section-container">
          <h2 className="final-cta-title">
            Stop Bill Increases From Becoming Financial Leaks
          </h2>
          <p className="final-cta-subtitle">
            Join early access and help shape a proactive financial watchdog that identifies recurring cost 
            increases before they quietly drain your finances.
          </p>
          <div className="final-cta-buttons">
            <Button 
              onClick={() => document.getElementById('bill-waitlist')?.scrollIntoView({ behavior: 'smooth' })}
              className="final-cta-btn"
              size="lg"
            >
              Join Early Access
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={() => handleDemoClick(FIGMA_DEMO_URL)}
              variant="outline"
              className="final-demo-btn"
              size="lg"
            >
              Try Interactive Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Feedback Popup */}
      <FeedbackPopup
        isOpen={showFeedbackPopup}
        onClose={() => setShowFeedbackPopup(false)}
        onSubmit={handleFeedbackSubmit}
      />
    </div>
  );
};

export default BillAlertsPage;
